<template>
  <div ref="gaugeContainer" class="radial-gauge-container" :class="{ 'radial-gauge-mini': !showLabels }">
    <div v-if="value === null || value === undefined" class="no-data">
      <slot name="no-data">No data</slot>
    </div>
  </div>
</template>

<script>
import { formatNum } from '@/util/number';

export default {
  name: 'RadialGaugeChart',
  props: {
    value: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 1600,
    },
    label: {
      type: String,
      default: 'RPM',
    },
    color: {
      type: String,
      default: '#0084ff',
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '200px',
    },
    // false = angka & nama dirender di luar (HTML)
    showLabels: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      chart: null,
      isChartInitialized: false,
    };
  },
  watch: {
    value() {
      this.updateChart();
    },
    color() {
      this.updateChart();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart();
    });
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    initChart() {
      if (!window.ApexCharts) {
        console.warn('[RadialGaugeChart] ApexCharts is not available');
        return;
      }

      if (this.isChartInitialized) return;

      const options = {
        series: [this.getPercentage()],
        chart: {
          width: this.width,
          height: this.height,
          type: 'radialBar',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
        },
        plotOptions: {
          radialBar: {
            hollow: {
              margin: 0,
              size: '70%',
              background: 'transparent',
            },
            track: {
              show: true,
              background: '#e0e0e0',
              strokeWidth: '100%',
              opacity: 1,
              margin: 5,
            },
            startAngle: -135,
            endAngle: 135,
            dataLabels: {
              show: this.showLabels,
              name: {
                fontSize: '14px',
                color: '#6c757d',
                offsetY: 110,
                fontFamily: 'inherit',
                fontWeight: 600,
              },
              value: {
                offsetY: 70,
                fontSize: '22px',
                color: this.color,
                formatter: (val) => {
                  const actualValue = (val / 100) * this.max;
                  return formatNum(actualValue, 2);
                },
                fontFamily: 'inherit',
                fontWeight: 700,
              },
              showAlways: true,
            },
          },
        },
        fill: {
          type: 'solid',
          colors: [this.color],
        },
        stroke: {
          lineCap: 'round',
          dashArray: 0,
        },
        labels: [this.label],
        colors: [this.color],
      };

      // mini: ring penuhin kotak, tanpa padding bawaan
      if (!this.showLabels) {
        options.chart.sparkline = { enabled: true };
        options.plotOptions.radialBar.hollow.size = '58%';
        options.plotOptions.radialBar.track.margin = 1;
      }

      try {
        this.chart = new window.ApexCharts(
          this.$refs.gaugeContainer,
          options,
        );
        this.isChartInitialized = true;
        // render() itu promise, error-nya gak ketangkep try/catch
        this.chart.render().catch((error) => {
          console.error('[RadialGaugeChart] Error rendering chart:', error?.message);
        });
      } catch (error) {
        console.error('[RadialGaugeChart] Error initializing chart:', error?.message);
      }
    },
    getPercentage() {
      return this.max > 0 ? ((this.value || 0) / this.max) * 100 : 0;
    },
    updateChart() {
      if (!this.chart || !this.isChartInitialized) return;

      this.chart.updateSeries([this.getPercentage()]);
    },
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
        this.isChartInitialized = false;
      }
    },
  },
};
</script>

<style scoped>
.radial-gauge-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 180px;
}

.radial-gauge-container.radial-gauge-mini {
  min-height: 0;
}

.no-data {
  color: #6c757d;
  font-size: 0.875rem;
  text-align: center;
}
</style>
